import { DOCUMENT } from '@angular/common';
import {
  EnvironmentProviders,
  inject,
  InjectionToken,
  Injectable,
  provideAppInitializer,
} from '@angular/core';
import {
  IDecodedToken,
  IImpersonationResponse,
  ILoginResponse,
} from '@inshuwa-client/models';
import { JWTDecoder } from '../utils/globals.utils';
import {
  getStoredLoginResponse,
  setStoredLoginResponse,
} from '../utils/auth-storage';
import { Router } from '@angular/router';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { sessionStore } from '../utils/session-store';
import { SharedAuthService } from './auth.service';

interface IStoredImpersonationSession {
  impersonatedUserId: string;
  startedAt: string;
}

const IMPERSONATION_STORAGE_KEY = 'impersonationSession';
const IMPERSONATION_TOKEN_QUERY_PARAM = 'icImpersonationToken';
export const IMPERSONATION_READ_ONLY_MODE = new InjectionToken<boolean>(
  'IMPERSONATION_READ_ONLY_MODE',
  {
    factory: () => false,
  },
);

@Injectable({
  providedIn: 'root',
})
export class ImpersonationService {
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly readOnlyModeEnabled = inject(IMPERSONATION_READ_ONLY_MODE);
  private readonly authService = inject(SharedAuthService);
  private readonly notification = inject(NzNotificationService);
  private expiryTimer: ReturnType<typeof setTimeout> | undefined;

  startImpersonationSession(response: IImpersonationResponse): void {
    const token = this.getResponseToken(response);
    const redirectUrl = this.buildRedirectUrl(response.redirectUrl, token);
    window.open(redirectUrl, '_blank', 'noopener,noreferrer');
  }

  applyImpersonationFromUrl(): void {
    const currentUrl = new URL(this.document.location.href);

    const hashParams = new URLSearchParams(currentUrl.hash.replace(/^#/, ''));
    const encodedToken = hashParams.get(IMPERSONATION_TOKEN_QUERY_PARAM);

    if (!encodedToken) {
      return;
    }
    const cleanFragment = (): void => {
      this.document.defaultView?.history.replaceState(
        {},
        this.document.title,
        currentUrl.pathname + currentUrl.search,
      );
    };

    const token = this.decodeRedirectToken(encodedToken);

    if (!token) {
      this.clearSession();
      cleanFragment();
      return;
    }

    // Marked as impersonation first, so the token never becomes the copy new tabs start from.
    this.saveSession(token);
    setStoredLoginResponse(token);
    cleanFragment();
    this.router.navigateByUrl('dashboard').then();
  }

  exit(): void {
    this.clearSession();
    // The tab was opened with window.open, so browsers normally allow closing it again.
    this.document.defaultView?.close();
    // Still open: not script-closable, so end this tab's session and return to login.
    this.authService.logOut(false);
  }

  isImpersonating(): boolean {
    const decoded = this.getSessionToken();
    return !!decoded && !this.isExpired(decoded);
  }


  watchExpiry(): void {
    const check = (): void => {
      clearTimeout(this.expiryTimer);
      const decoded = this.getSessionToken();
      if (!decoded) {
        return;
      }
      const remaining = decoded.exp * 1000 - Date.now();
      if (remaining <= 0) {
        this.endExpiredSession();
        return;
      }
      this.expiryTimer = setTimeout(check, remaining);
    };

    check();
    // Timers don't fire while a laptop sleeps; re-check when the tab is shown again.
    this.document.addEventListener('visibilitychange', () => {
      if (!this.document.hidden) {
        check();
      }
    });
  }

  private endExpiredSession(): void {
    this.notification.info(
      'Impersonation ended',
      'The impersonation session has expired.',
    );
    this.clearSession();
    this.authService.logOut(false);
  }

  private isExpired(decoded: IDecodedToken): boolean {
    return decoded.exp * 1000 <= Date.now();
  }

  /** The impersonated user's decoded token, when this tab holds a matching impersonation session. */
  private getSessionToken(): IDecodedToken | null {
    const session = this.getStoredSession();
    const activeToken = getStoredLoginResponse();

    if (!session || !activeToken) {
      this.clearSession();
      return null;
    }
    const decoded = JWTDecoder(activeToken.accessToken);
    if (!decoded?.id || decoded.id !== session.impersonatedUserId) {
      this.clearSession();
      return null;
    }

    return decoded;
  }

  isReadOnlyImpersonation(): boolean {
    return this.readOnlyModeEnabled && this.isImpersonating();
  }

  getImpersonatedUser(): IDecodedToken | null {
    const token = getStoredLoginResponse();

    if (!this.isImpersonating() || !token) {
      return null;
    }

    return JWTDecoder(token.accessToken);
  }

  private getResponseToken(response: IImpersonationResponse): ILoginResponse {
    const token = response.authToken;

    if (!token) {
      throw new Error('Impersonation response did not include an auth token');
    }
    return {
      accessToken: token,
      refreshToken: token,
    };
  }

  private buildRedirectUrl(redirectUrl: string, token: ILoginResponse): string {
    const targetUrl = new URL(redirectUrl, this.document.location.origin);

    // // Reject non-same-origin URLs to prevent token exfiltration via open redirects.
    // if (targetUrl.origin !== this.document.location.origin) {
    //   throw new Error(
    //     'Impersonation redirect URL must be same-origin. ' +
    //       `Expected origin "${this.document.location.origin}", got "${targetUrl.origin}".`,
    //   );
    // }
    //
    const fragment = new URLSearchParams();
    fragment.set(IMPERSONATION_TOKEN_QUERY_PARAM, btoa(JSON.stringify(token)));
    targetUrl.hash = fragment.toString();

    return targetUrl.toString();
  }

  private decodeRedirectToken(encodedToken: string): ILoginResponse | null {
    try {
      const parsedToken = JSON.parse(
        atob(encodedToken),
      ) as Partial<ILoginResponse> | null;

      if (
        !parsedToken ||
        typeof parsedToken.accessToken !== 'string' ||
        typeof parsedToken.refreshToken !== 'string'
      ) {
        return null;
      }

      return {
        accessToken: parsedToken.accessToken,
        refreshToken: parsedToken.refreshToken,
      };
    } catch {
      return null;
    }
  }

  private saveSession(token: ILoginResponse): void {
    const decoded = JWTDecoder(token.accessToken);

    if (!decoded?.id) {
      throw new Error(
        'Cannot save impersonation session: JWT does not contain a user id.',
      );
    }

    const session: IStoredImpersonationSession = {
      impersonatedUserId: decoded.id,
      startedAt: new Date().toISOString(),
    };

    sessionStore.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(session));
  }

  private getStoredSession(): IStoredImpersonationSession | null {
    const session = sessionStore.getItem(IMPERSONATION_STORAGE_KEY);

    return session
      ? (JSON.parse(session) as IStoredImpersonationSession)
      : null;
  }

  private clearSession(): void {
    sessionStore.removeItem(IMPERSONATION_STORAGE_KEY);
  }
}

export function provideImpersonationMode(): EnvironmentProviders {
  return provideAppInitializer(() => {
    const impersonation = inject(ImpersonationService);
    impersonation.applyImpersonationFromUrl();
    impersonation.watchExpiry();
  });
}
